import { useState, useEffect } from "react";
import { db, firebase } from "./Firebase";
import { updateUserFleets } from "./User";
import { useGetAllCompanies } from "./Company";
import { toast } from "react-toastify";

/**
 * ANCHOR Create Fleet
 */
const createFleet = (companyId, name, customerName, location, drivers, fleetManager) =>
  new Promise((resolve, reject) => {
    const fleetRef = db.collection(`companies/${companyId}/fleets/`);
    fleetRef
      .add({
        name,
        customerName,
        location,
        fleetManager,
        drivers,
        stats: {
          averageEnergyLevel: 100,
          averageTimeMostFatigued: "N/A"
        },
        created: Date.now()
      })
      .then(fleet => {
        drivers.forEach(driver => {
          updateUserFleets(driver.id, db.collection(`companies/${companyId}/fleets/`).doc(fleet.id), name);
        });

        return fleet;
      })
      .then(fleet => {
        if (fleetManager) {
          const newFleet = db.collection(`companies/${companyId}/fleets/`).doc(fleet.id);
          db.doc(`/users/${fleetManager.id}`).update({
            managingFleets: firebase.firestore.FieldValue.arrayUnion(newFleet)
          });
        }
      })
      .then(() => {
        toast("Fleet Created", { type: toast.TYPE.INFO });
        resolve(`Created fleet`);
      })
      .catch(error => {
        // Failed to create company within Firebase.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });

/**
 * ANCHOR Update Fleet
 */
const updateFleet = (
  companyId,
  fleetId,
  name,
  customerName,
  location,
  drivers,
  fleetManager,
  previousManager,
  previousDrivers
) =>
  new Promise((resolve, reject) => {
    const updatedDrivers = [...Array.from(drivers, driver => driver.id)];
    const origDrivers = [...Array.from(previousDrivers, driver => driver.uid)];

    const removedDrivers = [...origDrivers.filter(prevDriver => !updatedDrivers.includes(prevDriver))];
    const fleetRef = db.collection(`companies/${companyId}/fleets/`).doc(fleetId);

    fleetRef
      .set(
        {
          name,
          customerName,
          location,
          fleetManager,
          drivers
        },
        { merge: true }
      )
      .then(() => {
        drivers.forEach(driver => {
          updateUserFleets(driver.id, db.collection(`companies/${companyId}/fleets/`).doc(fleetId), name);
        });
      })
      .then(() => {
        removedDrivers.forEach(driver => {
          db.doc(`/users/${driver}`).set(
            {
              fleet: "",
              fleetName: ""
            },
            { merge: true }
          );
        });
      })
      .then(async () => {
        // Update Fleet Manager
        if (fleetManager && previousManager) {
          if (previousManager.id !== fleetManager.id) {
            db.doc(`/users/${fleetManager.id}`).update({
              managingFleets: firebase.firestore.FieldValue.arrayUnion(fleetRef)
            });
            db.doc(previousManager.path).update({
              managingFleets: firebase.firestore.FieldValue.arrayRemove(fleetRef)
            });
          }
        } else {
          if (fleetManager) {
            db.doc(`/users/${fleetManager.id}`).update({
              managingFleets: firebase.firestore.FieldValue.arrayUnion(fleetRef)
            });
          }
          if (previousManager) {
            db.doc(previousManager.path).update({
              managingFleets: firebase.firestore.FieldValue.arrayRemove(fleetRef)
            });
          }
        }
      })
      .then(() => {
        toast("Fleet Updated", { type: toast.TYPE.INFO });
        resolve(`Updated Fleet ${fleetId}`);
      })
      .catch(error => {
        // Failed to update Fleet document.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });

/**
 * Get Fleet
 */
const getFleet = (fleetId, companyId) =>
  new Promise((resolve, reject) => {
    const fleetRef = db.doc(`companies/${companyId}/fleets/${fleetId}`);
    if (!fleetId) {
      reject(new Error("Cannot get fleet without fleetId"));
    }

    fleetRef
      .get()
      .then(fleet => resolve(fleet))
      .catch(error => {
        // Failed to fetch fleet from Firebase.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });

/**
 * ANCHOR Get Fleets
 */
const getAllFleets = (companyId, uid, limit) =>
  new Promise((resolve, reject) => {
    let fleetsRef = db.collection(`companies/${companyId}/fleets`);

    if (!companyId) {
      reject(new Error("Cannot get fleets without companyId"));
    }

    if (uid) {
      fleetsRef = fleetsRef.where("fleetManager", "==", db.doc(`users/${uid}`));
    }

    if (limit) {
      fleetsRef = fleetsRef.limit(limit);
    }

    fleetsRef
      .get()
      .then(fleets => {
        const allFleets = [];

        fleets.docs.map((fleet, index) => {
          const fleetData = fleet.data();
          fleetData.fid = fleet.id;
          allFleets.push(fleetData);

          if (index + 1 === fleets.docs.length) return resolve(allFleets);
        });
      })
      .catch(error => {
        // Failed to fetch fleet from Firebase.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });

/**
 * Delete Fleet
 */
const deleteFleet = (companyId, fleetId) =>
  new Promise((resolve, reject) => {
    getFleet(fleetId, companyId).then(async fleet => {
      if (fleet.data().drivers) {
        await Promise.all(
          fleet.data().drivers.map(async driver => {
            await driver.set({ fleet: "", fleetName: "" }, { merge: true });
          })
        );
      }

      if (fleet.data().fleetManager) {
        db.doc(fleet.data().fleetManager.path).update({
          managingFleets: firebase.firestore.FieldValue.arrayRemove(fleet.ref)
        });
      }
      db.doc(`companies/${companyId}/fleets/${fleetId}`)
        .delete()
        // .then(() => resolve(`Deleted fleet ${fleetId} from company ${companyId}`))
        .then(() => {
          toast("Fleet Deleted", { type: toast.TYPE.INFO });
          resolve();
        })
        .catch(error => {
          // Failed to delete fleet from company.
          const errorCode = error.code;
          const errorMessage = error.message;
          reject(new Error(`${errorMessage} (${errorCode})`));
        });
    });
  });

const getDriversForFleet = fleet => {
  return new Promise(async (resolve, reject) => {
    const fleetData = fleet.data();
    fleetData.fid = fleet.id;
    var drivers = [];
    var driverRefs = [];
    var sum = 0;
    var avg = 0;

    var moment = require("moment");

    await Promise.all(
      fleetData.drivers.map(async driverDoc => {
        if (driverDoc instanceof firebase.firestore.DocumentReference) {
          driverRefs.push(driverDoc);
          await driverDoc.get().then(driver => {
            var driverObj = driver.data();
            driverObj.fullName = `${driverObj.name.firstName} ${driverObj.name.lastName}`;
            var energyLevel = driver.data().stats.energyLevel;
            var energyUpdated = moment(driver.data().stats.energyUpdated);
            if (energyUpdated.isAfter(moment().subtract(30, "minutes"))) {
              sum += Number(energyLevel);
              driverObj.energyLevel = energyLevel;
            } else {
              driverObj.energyLevel = "N/A";
            }
            drivers.push(driverObj);
          });
        }
      })
    );

    fleetData.driverRefs = driverRefs;
    fleetData.drivers = drivers;
    fleetData.averageEnergyLevel = sum > 0 ? Math.round(sum / drivers.length) : 0;
    resolve(fleetData);
  });
};

const getNotificationsForFleet = (fleetId, companyId) => {
  const [notifications, setNotifications] = useState({ notifications: [] });

  const fleetRef = db.doc(`companies/${companyId}/fleets/${fleetId}`);

  useEffect(() => {
    db.collection("notifications")
      .where("fleet", "==", fleetRef)
      .onSnapshot(querySnapshot => setNotifications({ notifications: querySnapshot }));
  }, []);

  return notifications;
};

/**
 * ANCHOR Get Fleets
 */
const useGetAllFleets = (companyId, uid, limit) => {
  const [allFleets, setAllFleets] = useState({
    loading: true,
    fleets: []
  });

  const getFleetsForCompany = companyId => {
    return new Promise((resolve, reject) => {
      let fleetsRef = db.collection(`companies/${companyId}/fleets`);
      fleetsRef.onSnapshot(fleets => {
        resolve(fleets);
      });
    });
  };

  if (!companyId) {
    useEffect(() => {
      const companiesRef = db.collection("companies");

      companiesRef.onSnapshot(async companies => {
        var allFleetDocs = [];

        await Promise.all(
          companies.docs.map(async (company, index) => {
            var fleets = await getFleetsForCompany(company.id);
            await Promise.all(
              fleets.docs.map(async (fleet, index) => {
                var fleetObj = await getDriversForFleet(fleet);
                fleetObj.cid = company.id;
                fleetObj.companyName = company.data().name;
                allFleetDocs.push(fleetObj);
              })
            );
          })
        );

        return setAllFleets({
          loading: false,
          fleets: allFleetDocs
        });
      });
    }, []);
  } else {
    let fleetsRef = db.collection(`companies/${companyId}/fleets`);

    if (uid && uid !== null) {
      fleetsRef = fleetsRef.where("fleetManager", "==", db.doc(`users/${uid}`));
    }

    if (limit) {
      fleetsRef = fleetsRef.limit(limit);
    }

    useEffect(() => {
      fleetsRef.onSnapshot(async fleets => {
        const allFleetDocs = [];
        await Promise.all(
          fleets.docs.map(async (fleet, index) => {
            var fleetObj = await getDriversForFleet(fleet);
            fleetObj.cid = companyId;
            allFleetDocs.push(fleetObj);
          })
        );

        return setAllFleets({
          loading: false,
          fleets: allFleetDocs
        });
      });
    }, []);
  }
  return allFleets;
};

const useGetDriversForFleets = fleets => {
  const [drivers, setDrivers] = useState({
    drivers: []
  });
  if (!fleets) return drivers;
  useEffect(() => {
    var allDrivers = [];

    fleets.map((fleetRef, index) => {
      fleetRef.get().then(async fleet => {
        var fleetObj = await getDriversForFleet(fleet);
        allDrivers.push(...fleetObj.drivers);

        if (fleets.length === index + 1)
          return setDrivers({
            drivers: allDrivers
          });
      });
    });
  }, []);

  return drivers;
};

/**
 * Get Fleet
 */
const useGetFleet = (fleetId, companyId) => {
  const [fleet, setFleet] = useState({
    loading: true,
    fleet: null
  });

  const fleetRef = db.doc(`companies/${companyId}/fleets/${fleetId}`);

  if (!fleetId) {
    return null;
  }
  useEffect(() => {
    fleetRef.onSnapshot(async fleetDoc => {
      var fleetObj = await getDriversForFleet(fleetDoc);
      setFleet({
        loading: false,
        fleet: fleetObj
      });
    });
  }, []);

  return fleet;
};

export {
  createFleet,
  getFleet,
  getAllFleets,
  getNotificationsForFleet,
  deleteFleet,
  updateFleet,
  useGetAllFleets,
  useGetDriversForFleets,
  useGetFleet
};
